import { Box, FormHelperText, Stack, Typography } from '@mui/material'

import useAccessTokenAndShopId from '../../hooks/useAccessTokenAndShopId/useAccessTokenAndShopId'
import { EntryWithRelated } from '../../models/inspections'
import PromptInput from '../../pages/DviSingleInspectionEntry/PromptInput'
import { PromptInputForMultiChoice } from '../../pages/DviSingleInspectionEntry/PromptInputForMultiChoice'
import { PromptInputForMultiChoiceMultiSelect } from '../../pages/DviSingleInspectionEntry/PromptInputForMultiChoiceMultiSelect'
import InspectionEntryStatusSelect from '../../pages/DviSingleInspectionEntry/StatusSelect'
import { useUpdateInspectionPromptMutation } from '../../redux/api/inspectionApi'
import { PromptInfoTypeEnum } from '../../sdk'
import MediaGallery, { MediaEntryType } from '../MediaGallery/MediaGallery'
import { LoadingWrapper } from '../SharedUI/LoadingWrapper'
import { getPromptAnswer } from './getPromptAnswer'

export const EditSingleInspectionEntry = ({
  entry,
  submitStatusCallback,
  goBackOneEntry
}: {
  entry: EntryWithRelated
  submitStatusCallback?: () => void
  goBackOneEntry?: () => void
}) => {
  const { accessToken } = useAccessTokenAndShopId()
  const [updatePrompt, { isLoading }] = useUpdateInspectionPromptMutation()

  // after cache tag invalidation
  const onSubmit = async (id: string, value: string) => {
    const foundPrompt = entry.prompts.find((prompt) => id === prompt.id!)
    if (foundPrompt) {
      await updatePrompt({
        authorization: accessToken,
        promptEntry: {
          ...foundPrompt,
          info: {
            ...foundPrompt.info!,
            answer: value,
            multipleChoiceAnswer: undefined,
            multipleChoiceMultipleSelectAnswers: undefined
          }
        }
      })
    }
  }

  if (entry.prompts.length === 0) {
    return <></>
  }

  return (
    <LoadingWrapper loading={isLoading}>
      <Stack spacing={2}>
        <Typography component="h2" variant="h2">
          {entry.info!.title}
        </Typography>
        <Typography>{entry.info!.description}</Typography>
        <Stack direction="column" spacing={1}>
          {entry.prompts.map((prompt) => {
            const type = prompt.info!.type!
            if (type === PromptInfoTypeEnum.MultipleChoice) {
              return (
                <Box key={prompt.id!} p={1}>
                  <PromptInputForMultiChoice {...{ prompt, entry }} />
                </Box>
              )
            } else if (
              type === PromptInfoTypeEnum.MultipleChoiceMultipleSelect
            ) {
              return (
                <Box key={prompt.id!} p={1}>
                  <PromptInputForMultiChoiceMultiSelect
                    {...{ prompt, entry }}
                  />
                </Box>
              )
            } else {
              return (
                <Box key={prompt.id!} p={1}>
                  <PromptInput
                    label={
                      (prompt.info?.required ? '* ' : '') +
                      prompt.info!.question!
                    }
                    value={getPromptAnswer(prompt.info!) as string}
                    setValue={(value) => onSubmit(prompt.id!, value)}
                    type={prompt.info!.type!}
                  />
                </Box>
              )
            }
          })}
        </Stack>
        {entry.prompts[0].info!.requiresPhoto &&
          entry.prompts[0].media.length === 0 && (
            <FormHelperText error>* Photo is required</FormHelperText>
          )}
        <MediaGallery
          mediaEntryType={MediaEntryType.inspectionPrompt}
          media={entry.prompts[0].media}
          parentId={entry.prompts[0].id!}
          isMobileDevice={false}
        />
        <InspectionEntryStatusSelect
          entry={entry!}
          submitCallback={submitStatusCallback}
          goBackOneEntry={goBackOneEntry}
        />
      </Stack>
    </LoadingWrapper>
  )
}
